/**
 * Landing Page Builder V2 - Component field spec
 * Describes which fields of each `PageComponent` type are writable and how they nest (flat
 * primitive, image-only, ordered list, membership list, or nested object list). This is the
 * writable-shape vocabulary for `PageComponent` itself, not an AI-specific concept — it's
 * consumed both by the landing-page-copilot feature's path-based tool calls (`updateComponent`/
 * `suggestImage`) and by this feature's own `duplicateComponent` (`regenerate-nested-ids.ts`,
 * which has nothing to do with AI). It lives here, not in landing-page-copilot, so that neither
 * feature has to import the other's internals for it.
 */

import { detectLinkType } from "../link-utils";
import type { ComponentType, TeamMember } from "../../_types";
import {
  DIVIDER_HEIGHT_PRESETS,
  DIVIDER_LINE_STYLE_OPTIONS,
  FOOTER_ALIGNMENT_VALUES,
  HERO_HEIGHT_VARIANT_OPTIONS,
  MAX_DIVIDER_LINE_THICKNESS_PX,
  MIN_DIVIDER_LINE_THICKNESS_PX,
} from "./builder";

/** One field's writable shape within a component-type's spec tree. */
export type PathFieldSpec =
  | { kind: "leaf" }
  | { kind: "imageLeaf" }
  /** An `IconEmojiValue` (`{type: "icon"|"emoji", value}`) written as one encoded string —
   *  `"icon:<curated name>"` or `"emoji:<curated character>"` — instead of widening
   *  `updateComponentInputSchema`'s primitive-only value union for this one object-shaped field.
   *  `path-utils.ts`'s `applyToNode` decodes and validates the name/character against the curated
   *  icon/emoji lists before writing the `{type, value}` object; `null` clears the field. */
  | { kind: "iconLeaf" }
  /** A `leaf` that's writable only some of the time — a sibling field on the same item makes this
   *  one's manually-set value meaningless (something else overrides it) once set. `forbiddenWhen`
   *  is evaluated against the item's own current fields (never a network fetch, so it can only see
   *  what's already on the node — e.g. whether a form is linked, not whether that form actually
   *  has payment configured); a non-null return is the rejection reason, `null` allows the write. */
  | { kind: "conditionalLeaf"; forbiddenWhen: (node: Record<string, unknown>) => string | null }
  /** A `leaf` restricted to a fixed set of values (e.g. a narrow number/string union in the
   *  `PageComponent` type) — `updateComponentInputSchema`'s value union is primitive-only and
   *  can't express the constraint itself, so `path-utils.ts`'s `applyToNode` rejects anything not
   *  in `values` before writing. */
  | { kind: "enumLeaf"; values: readonly (string | number | boolean)[] }
  /** A `leaf` restricted to a hex color, for fields whose value reaches an inline `style`. Same
   *  reasoning as `enumLeaf`, for a set too large to enumerate: React doesn't escape `;` in a
   *  style value during SSR, so an unvalidated string is a CSS injection on the published page.
   *  The renderers validate before rendering, but that leaves the bad value persisted in
   *  `layout_tree`, which this stops at the write boundary instead.
   *
   *  Note: the pre-existing color fields (`navbar.customColor`, `footer.customColor`,
   *  `schedule.labels[].color`, `pricing` card colors) are still plain `leaf` and should move to
   *  this kind too — deliberately left for its own change rather than swept in here. */
  | { kind: "colorLeaf" }
  | { kind: "primitiveArray"; addressing: "index" }
  | {
      kind: "primitiveArray";
      addressing: "value";
      /** Name of the sibling-or-ancestor `objectArray` field whose item ids this list's values
       *  reference (e.g. a team member's `labelIds` references `labels`) — lets
       *  `regenerateNestedIds` remap these values when the referenced array's ids are
       *  regenerated (on `duplicateComponent`), instead of leaving stale references behind. */
      referencesArrayField?: string;
    }
  | {
      kind: "objectArray";
      fields: Record<string, PathFieldSpec>;
      /** Factory for a brand-new item's default shape (always stamps a fresh `id`). */
      defaults: () => Record<string, unknown>;
      /** True for arrays whose item shape requires a real image URL with no legitimate way to
       *  supply one from chat text (venue/carousel galleries) — the copilot's `updateComponent`
       *  may edit/delete an existing item but never create one; only `suggestImage` (which always
       *  re-uploads a real found photo) can create a new item here. */
      createOnlyViaImageTool?: boolean;
      /** Re-derives one or more of this array's own `forbidden` fields from its other fields (e.g.
       *  `TeamMember.linkType` from `link`) after every write to this component, regardless of
       *  which field the write actually touched — the only place a chat-created/edited item's
       *  derived field gets set, since the field itself can never be written directly. Mutates
       *  `items` in place. */
      deriveAfterWrite?: (items: Record<string, unknown>[]) => void;
    }
  | { kind: "forbidden"; reason: string };

/** A component type's full writable-field spec, keyed by top-level field name. */
export type ComponentFieldSpec = Record<string, PathFieldSpec>;

/** Shorthand for the two field kinds every component type's base fields share. */
const leaf: PathFieldSpec = { kind: "leaf" };
const imageLeaf: PathFieldSpec = { kind: "imageLeaf" };
/** `title`/`navigationLabel`/`hiddenFromNav`/`navGroup` — the flat fields every `BaseComponent` has.
 *  `hiddenFromNav` is boolean-only (`enumLeaf` restricted to `true`/`false`, `null` always allowed
 *  to clear it) rather than a plain `leaf` — unlike most fields here it's read as a boolean by the
 *  navbar, so an unvalidated string/number write would silently break nav visibility. `navGroup` is
 *  a free-text label matched by exact string equality across components (not an id) — reuse an
 *  existing section's value verbatim to add it to that dropdown group. */
const BASE_FIELDS: Record<string, PathFieldSpec> = {
  title: leaf,
  navigationLabel: leaf,
  hiddenFromNav: { kind: "enumLeaf", values: [true, false] },
  navGroup: leaf,
};

/** `CtaButton` sub-field spec, shared by every host type (`navbar`/`hero`/`textButton`). */
const CTA_BUTTON_FIELDS: Record<string, PathFieldSpec> = { label: leaf, url: leaf };
const ctaButtonsSpec = (): PathFieldSpec => ({
  kind: "objectArray",
  fields: CTA_BUTTON_FIELDS,
  defaults: () => ({ id: crypto.randomUUID(), label: "", url: "" }),
});

/** Re-derives every team member's `linkType` from their (possibly just-edited) `link`, the same
 *  way `EditTeamMemberModal.tsx`'s manual save handler always does — `team.members.linkType` is
 *  `forbidden` (it can't be allowed to drift from `link`), so this is the only place a chat-
 *  created/edited member's icon gets set. */
function deriveTeamMemberLinkTypes(items: Record<string, unknown>[]): void {
  for (const member of items as unknown as TeamMember[]) {
    member.linkType = detectLinkType(member.link ?? "");
  }
}

/** `ComponentImage` sub-field spec (venue/carousel galleries) — `name` is chat-editable, `url`
 *  is `suggestImage`-only, and the array itself can only gain a *new* item via `suggestImage`
 *  (there's no legitimate way to hand it a real image URL from chat text). */
const galleryImagesSpec = (): PathFieldSpec => ({
  kind: "objectArray",
  fields: { name: leaf, url: imageLeaf },
  defaults: () => ({ id: crypto.randomUUID(), name: "", url: "" }),
  createOnlyViaImageTool: true,
});

/**
 * Per-component-type writable-field spec — replaces the former `UPDATE_COMPONENT_ALLOWED_FIELDS`/
 * `SUGGEST_IMAGE_ALLOWED_FIELDS` pair with one tree consumed by both of the copilot's tools
 * (`path-utils.ts`'s `applyToNode`, gated by `forImageTool`) and by `regenerateNestedIds`. Fields
 * not listed here (or marked `forbidden`) are unreachable from chat regardless of tool — `id`/
 * `type` are never included, so a call that tries to smuggle either is rejected the same way as
 * any invented field name.
 *
 * Deliberate scope cuts are always `forbidden` with a message rather than silently absent, so a
 * future reader doesn't mistake an unlisted field for an oversight:
 *  - `CardGridItem.background` (`CardBackground` discriminated union) — visual/design property,
 *    non-trivial union semantics, manual-only.
 *  - `TeamMember.linkType` — always derived from `link` via `detectLinkType` in `link-utils.ts`
 *    (also called directly by the manual editor's own save handler), never set independently, so
 *    it can't drift from the link it's supposed to describe. The copilot path re-derives it after
 *    every write via `team.members`' own `deriveAfterWrite` (`deriveTeamMemberLinkTypes` above).
 *  - `PricingCard.price` — a `conditionalLeaf`: forbidden while the card has a linked `formId`
 *    (price is pulled from the form once linked, so a chat edit would just be overridden), but
 *    writable like a plain `leaf` for a card with no linked form, matching `EditPricingCardModal`'s
 *    own manual-price input (which is only hidden once the linked form actually has payment
 *    configured — a narrower condition this can't check without a network fetch, so this errs
 *    toward forbidding whenever a form is linked at all).
 * `CardGridItem.icon` (an `IconEmojiValue`, `{type, value}`) is writable via the `iconLeaf` kind
 * instead of a cut — see the `PathFieldSpec` union above for its encoded-string shape and
 * `decodeIconLeafValue` in `path-utils.ts` for the curated-list validation.
 */
export const COMPONENT_FIELD_SPEC: Record<ComponentType, ComponentFieldSpec> = {
  navbar: {
    ...BASE_FIELDS,
    visible: leaf,
    ctaText: leaf,
    ctaLink: leaf,
    logoUrl: imageLeaf,
    iconUrl: imageLeaf,
    logoLink: leaf,
    colorMode: leaf,
    customColor: leaf,
    buttonColorMode: leaf,
    buttonCustomColor: leaf,
    showSubpagesInNav: leaf,
    ctaButtons: ctaButtonsSpec(),
  },
  hero: {
    ...BASE_FIELDS,
    startDate: leaf,
    startTime: leaf,
    endDate: leaf,
    endTime: leaf,
    date: leaf,
    location: leaf,
    description: leaf,
    backgroundMedia: imageLeaf,
    backgroundMediaType: leaf,
    overlayOnBackground: leaf,
    showCtaButtons: leaf,
    ctaButtons: ctaButtonsSpec(),
    heightVariant: { kind: "enumLeaf", values: HERO_HEIGHT_VARIANT_OPTIONS.map((o) => o.value) },
  },
  countdown: {
    ...BASE_FIELDS,
    targetDate: leaf,
    targetTime: leaf,
    timeZone: leaf,
    expiredText: leaf,
  },
  faq: {
    ...BASE_FIELDS,
    description: leaf,
    items: {
      kind: "objectArray",
      fields: { question: leaf, answer: leaf },
      defaults: () => ({ id: crypto.randomUUID(), question: "", answer: "" }),
    },
  },
  venue: {
    ...BASE_FIELDS,
    address: leaf,
    description: leaf,
    showButton: leaf,
    buttonText: leaf,
    buttonLink: leaf,
    mediaMode: leaf,
    images: galleryImagesSpec(),
  },
  schedule: {
    ...BASE_FIELDS,
    description: leaf,
    daySummaries: {
      kind: "objectArray",
      fields: { date: leaf, text: leaf },
      defaults: () => ({ id: crypto.randomUUID(), date: "", text: "" }),
    },
    agendaSummariesOnly: { kind: "enumLeaf", values: [true, false] },
    agendaOneDayAtATime: { kind: "enumLeaf", values: [true, false] },
  },
  richText: { ...BASE_FIELDS, content: leaf },
  activities: { ...BASE_FIELDS, description: leaf },
  textButton: {
    ...BASE_FIELDS,
    description: leaf,
    showButton: leaf,
    buttonText: leaf,
    buttonLink: leaf,
    ctaButtons: ctaButtonsSpec(),
  },
  carousel: { ...BASE_FIELDS, description: leaf, images: galleryImagesSpec() },
  video: { ...BASE_FIELDS, videoUrl: leaf, caption: leaf },
  team: {
    ...BASE_FIELDS,
    description: leaf,
    labels: {
      kind: "objectArray",
      fields: { name: leaf, color: leaf },
      defaults: () => ({ id: crypto.randomUUID(), name: "" }),
    },
    members: {
      kind: "objectArray",
      fields: {
        name: leaf,
        title: leaf,
        link: leaf,
        linkType: {
          kind: "forbidden",
          reason: "linkType is derived from link automatically — set link instead",
        },
        email: leaf,
        phone: leaf,
        imageUrl: imageLeaf,
        labelIds: { kind: "primitiveArray", addressing: "value", referencesArrayField: "labels" },
      },
      defaults: () => ({
        id: crypto.randomUUID(),
        name: "",
        title: "",
        imageUrl: "",
        link: "",
        linkType: "other",
        labelIds: [],
      }),
      deriveAfterWrite: deriveTeamMemberLinkTypes,
    },
  },
  pricing: {
    ...BASE_FIELDS,
    items: {
      kind: "objectArray",
      fields: {
        title: leaf,
        subtitle: leaf,
        price: {
          kind: "conditionalLeaf",
          forbiddenWhen: (item) =>
            item.formId
              ? "price is pulled from the linked form — link/edit the form instead of setting a price directly"
              : null,
        },
        badge: leaf,
        description: leaf,
        ctaText: leaf,
        ctaLink: leaf,
        color: leaf,
        formId: leaf,
        respondentTypeId: leaf,
        imageUrl: imageLeaf,
        features: { kind: "primitiveArray", addressing: "index" },
      },
      defaults: () => ({ id: crypto.randomUUID(), title: "" }),
    },
  },
  cardGrid: {
    ...BASE_FIELDS,
    description: leaf,
    layout: leaf,
    showNumbers: leaf,
    descriptionDisplay: leaf,
    columns: { kind: "enumLeaf", values: [2, 3] },
    items: {
      kind: "objectArray",
      fields: {
        title: leaf,
        description: leaf,
        interactionType: leaf,
        href: leaf,
        buttonLabel: leaf,
        icon: { kind: "iconLeaf" },
        background: {
          kind: "forbidden",
          reason: "Card backgrounds are manual-only for now — set it in the Editor tab",
        },
      },
      defaults: () => ({ id: crypto.randomUUID(), icon: null, title: "", description: "", interactionType: "none" }),
    },
  },
  sponsors: {
    ...BASE_FIELDS,
    description: leaf,
    showTiers: leaf,
    tierOrder: { kind: "primitiveArray", addressing: "index" },
    sponsors: {
      kind: "objectArray",
      fields: { name: leaf, bio: leaf, sponsorNote: leaf, websiteUrl: leaf, tier: leaf, logoUrl: imageLeaf },
      defaults: () => ({ id: crypto.randomUUID(), name: "", logoUrl: "" }),
    },
  },
  divider: {
    ...BASE_FIELDS,
    // A divider is structurally excluded from navigation (see `isNavigableSection`) because it has
    // no addressable content — so every nav field here is inert, and `navigationLabel` is worse
    // than inert: setting it makes `getAnchorId` mint a real anchor id that nothing can ever link
    // to. Forbidden rather than silently ignored, so the copilot gets told why instead of
    // reporting a successful write that changes nothing the organizer can see.
    navigationLabel: { kind: "forbidden", reason: "A divider is decorative and never appears in navigation" },
    hiddenFromNav: { kind: "forbidden", reason: "A divider is decorative and never appears in navigation" },
    navGroup: { kind: "forbidden", reason: "A divider is decorative and never appears in navigation" },
    // Restricted to the same Small/Medium/Large presets the manual editor offers — the AI chat
    // path shouldn't be able to set an arbitrary pixel height the UI itself no longer allows.
    height: { kind: "enumLeaf", values: DIVIDER_HEIGHT_PRESETS.map((p) => p.height) },
    // The rest of the rule's appearance, held to the same bounds the editor enforces. All four
    // reach an inline `style`; the renderer validates them before rendering, but without these a
    // junk value still persists into `layout_tree` and the editor then shows it back as truth.
    showLine: { kind: "enumLeaf", values: [true, false] },
    lineColor: { kind: "colorLeaf" },
    lineThickness: {
      kind: "enumLeaf",
      values: Array.from(
        { length: MAX_DIVIDER_LINE_THICKNESS_PX - MIN_DIVIDER_LINE_THICKNESS_PX + 1 },
        (_, i) => MIN_DIVIDER_LINE_THICKNESS_PX + i
      ),
    },
    lineStyle: { kind: "enumLeaf", values: DIVIDER_LINE_STYLE_OPTIONS.map((o) => o.value) },
  },
  footer: {
    ...BASE_FIELDS,
    logoUrl: imageLeaf,
    logoLink: leaf,
    tagline: leaf,
    showSectionsColumn: leaf,
    sectionsColumnHeading: leaf,
    showSubpagesColumn: leaf,
    subpagesColumnHeading: leaf,
    colorMode: leaf,
    customColor: leaf,
    alignment: { kind: "enumLeaf", values: FOOTER_ALIGNMENT_VALUES },
    columns: {
      kind: "objectArray",
      fields: {
        heading: leaf,
        links: {
          kind: "objectArray",
          fields: { label: leaf, url: leaf },
          defaults: () => ({ id: crypto.randomUUID(), label: "", url: "" }),
        },
      },
      defaults: () => ({ id: crypto.randomUUID(), heading: "", links: [] }),
    },
    socialLinks: {
      kind: "objectArray",
      fields: { url: leaf, type: leaf },
      defaults: () => ({ id: crypto.randomUUID(), url: "", type: "other" }),
    },
  },
};
